import { GroupDetail_Form_Type, UserDetail, WebSocketMessage } from '@policyfly/protobuf/patrick'
import { isJWTValid } from '@policyfly/utils/api'
import { generateUUID } from '@policyfly/utils/uuid'
import { syncRef, useWebSocket } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, reactive, ref, watch } from 'vue'

import { useAuthenticationStore } from '@/stores/authentication'

import { useApiStore } from './api'
import { hotReloadStore } from '@/utils/build'

import type {
  ConnectionDetail,
  GroupDetail,
  WebSocketMessage_GroupUpdate,
  WebSocketMessage_ScheduleCell,
  WebSocketMessage_ScheduleItemCreate,
  WebSocketMessage_ScheduleItemUpsert,
  WebSocketMessage_ScheduleItemDelete,
} from '@policyfly/protobuf/patrick'
import type { DistributiveOmit, MakeKeysOptional, MaybePromise } from '@policyfly/types/utils'
import type { WebSocketStatus } from '@vueuse/core'
import type { ComputedRef } from 'vue'

/**
 * Maps group-scoped WebSocket events to their payloads.
 */
interface WebSocketGroupBroadcasts {
  groupUpdate: WebSocketMessage_GroupUpdate
  scheduleCell: WebSocketMessage_ScheduleCell
  scheduleItemCreate: WebSocketMessage_ScheduleItemCreate
  scheduleItemUpsert: WebSocketMessage_ScheduleItemUpsert
  scheduleItemDelete: WebSocketMessage_ScheduleItemDelete
}
/**
 * Handles a message for a specific group-scoped WebSocket event.
 */
export type GroupMessageHandler<Event extends GroupEvent> = (message: WebSocketGroupBroadcasts[Event]) => MaybePromise<void>

/**
 * How long a "ghost" user will appear after they leave the group.
 */
const GHOST_PERSIST_DURATION = 30_000

type GroupEvent = keyof WebSocketGroupBroadcasts
interface GroupSubscriber {
  location: GroupDetail['location']
  handler: GroupMessageHandler<GroupEvent>
}
type GroupEventPayload<Event extends GroupEvent> = DistributiveOmit<WebSocketGroupBroadcasts[Event], 'connectionDetail' | 'groupDetail'>
type GroupEventHandler<Event extends GroupEvent> = (event: GroupEventPayload<Event>) => MaybePromise<void>

export interface WebSocketGroupUser extends ConnectionDetail {
  /**
   * When a user leaves they are only removed after a delay.
   * During this time they are marked as a "ghost".
   */
  ghost?: boolean
}

export interface WebSocketGroup extends GroupDetail {
  users: WebSocketGroupUser[]
}

export function isSameGroupLocation (
  a: GroupDetail['location'] | undefined,
  b: GroupDetail['location'] | undefined,
): boolean {
  if (!a || !b) return false
  if (a.oneofKind !== b.oneofKind) return false

  switch (a.oneofKind) {
    case 'application':
      return b.oneofKind === 'application' && a.application.id === b.application.id
    case 'schedule':
      return b.oneofKind === 'schedule' && a.schedule.id === b.schedule.id
    case 'form':
      return a.form.type !== GroupDetail_Form_Type.TYPE_UNSPECIFIED &&
        b.oneofKind === 'form' &&
        a.form.id === b.form.id &&
        a.form.type === b.form.type
    default:
      return false
  }
}

export type GroupUpdatePayload = MakeKeysOptional<
  Omit<WebSocketMessage_GroupUpdate, 'groupDetail' | 'connectionDetail'>,
  | 'onlyRoles'
>

export interface WebSocketStore {
  /**
   * The current user's uuid.
   */
  uuid: string
  /**
   * The current connection status.
   * See {@link WebSocketStatus}.
   */
  status: ComputedRef<WebSocketStatus>
  /**
   * The details of groups the user is part of.
   */
  groups: ComputedRef<WebSocketGroup[]>
  /**
   * Starts the connection to the WebSocket service.
   * This is intended to last the duration of the session.
   */
  connect: () => void
  /**
   * Stops the connection.
   */
  disconnect: () => void
  /**
   * Joins a group based on the provided location.
   */
  join: (location: GroupDetail['location']) => WebSocketGroup | undefined
  /**
   * Leaves a group based on the provided location.
   */
  leave: (location: GroupDetail['location']) => void
  /**
   * Sends a group update to all users currently in the given group.
   */
  sendGroupUpdate: (
    location: GroupDetail['location'],
    payload: GroupUpdatePayload,
  ) => void
  /**
   * Sends a schedule-cell selection to all users currently in the given group.
   */
  sendScheduleCell: (
    location: GroupDetail['location'],
    payload: Omit<WebSocketMessage_ScheduleCell, 'groupDetail' | 'connectionDetail'>,
  ) => void
  /**
   * Sends a schedule item creation to all users currently in the given group.
   */
  sendScheduleItemCreate: (
    location: GroupDetail['location'],
    payload: Omit<WebSocketMessage_ScheduleItemCreate, 'groupDetail' | 'connectionDetail'>,
  ) => void
  /**
   * Sends a schedule item upsert to all users currently in the given group.
   */
  sendScheduleItemUpsert: (
    location: GroupDetail['location'],
    payload: Omit<WebSocketMessage_ScheduleItemUpsert, 'groupDetail' | 'connectionDetail'>,
  ) => void
  /**
   * Sends a schedule item delete to all users currently in the given group.
   */
  sendScheduleItemDelete: (
    location: GroupDetail['location'],
    payload: Omit<WebSocketMessage_ScheduleItemDelete, 'groupDetail' | 'connectionDetail'>,
  ) => void
  /**
   * Subscribes to a specific group event for a given location.
   * Returns an unsubscribe function.
   */
  subscribe: <Event extends GroupEvent>(
    event: Event,
    location: GroupDetail['location'],
    handler: GroupEventHandler<Event>,
  ) => () => void
}

export const useWebSocketStore = defineStore('webSocket', (): WebSocketStore => {
  const uuid = generateUUID()
  const authenticationStore = useAuthenticationStore()
  const program = computed(() => authenticationStore.currentProgram?.slug)

  function getConnectionDetail (): ConnectionDetail {
    return {
      uuid,
      userDetail: UserDetail.create({
        bingoId: authenticationStore.bingoId ?? '',
        firstName: authenticationStore.firstName,
        lastName: authenticationStore.lastName,
      }),
    }
  }

  const ws = ref<WebSocket | undefined>()
  const statusInternal = ref<WebSocketStatus>('CLOSED')
  const status: WebSocketStore['status'] = computed(() => statusInternal.value)

  const groupsInternal = reactive<WebSocketGroup[]>([])
  const groups: WebSocketStore['groups'] = computed(() => groupsInternal)
  function findGroup (group: GroupDetail): WebSocketGroup | undefined {
    return groupsInternal.find((g) => g.program === group.program && isSameGroupLocation(g.location, group.location))
  }

  const ghostTimeouts = new Map<WebSocketGroup, Map<string, number>>()
  function getTimeoutsGroup (group: WebSocketGroup): Map<string, number> {
    const existing = ghostTimeouts.get(group)
    if (existing) return existing
    const newTimeouts = new Map<string, number>()
    ghostTimeouts.set(group, newTimeouts)
    return newTimeouts
  }
  function scheduleGhostRemoval (group: WebSocketGroup, userUuid: string): void {
    const timeouts = getTimeoutsGroup(group)
    const existing = timeouts.get(userUuid)
    if (existing) window.clearTimeout(existing)
    timeouts.set(userUuid, window.setTimeout(() => {
      const index = group.users.findIndex((u) => u.uuid === userUuid && u.ghost)
      if (index > -1) group.users.splice(index, 1)
      timeouts.delete(userUuid)
    }, GHOST_PERSIST_DURATION))
  }
  function cancelGhostRemoval (group: WebSocketGroup, userUuid: string): void {
    const timeouts = ghostTimeouts.get(group)
    if (!timeouts) return
    const existing = timeouts.get(userUuid)
    if (!existing) return
    window.clearTimeout(existing)
    timeouts.delete(userUuid)
  }
  function clearGhostRemovals (targets: WebSocketGroup[]): void {
    for (const target of targets) {
      for (const timeout of ghostTimeouts.get(target)?.values() ?? []) window.clearTimeout(timeout)
      ghostTimeouts.delete(target)
    }
  }

  const subscribers = new Map<GroupEvent, Set<GroupSubscriber>>()

  async function notifySubscribers<Event extends GroupEvent> (
    event: Event,
    payload: GroupEventPayload<Event>,
  ): Promise<void> {
    const groupDetail = (payload as { groupDetail?: GroupDetail }).groupDetail
    for (const sub of subscribers.get(event) ?? []) {
      if (isSameGroupLocation(sub.location, groupDetail?.location)) await sub.handler(payload)
    }
  }

  const apiStore = useApiStore()
  function connectInternal (): void {
    disconnectInternal()
    if (!isJWTValid(apiStore.accessToken)) return
    if (!authenticationStore.bingoId) return
    const webSocketInstance = useWebSocket('/patrick.v1.PatrickService/ws', {
      heartbeat: {
        message: () => WebSocketMessage.toBinary(WebSocketMessage.create({
          event: {
            oneofKind: 'ping',
            ping: {},
          },
        })) as unknown as ArrayBuffer,
      },
      autoReconnect: {
        retries (retried) {
          if (retried > 10) return false
          if (!isJWTValid(apiStore.accessToken)) return false
          if (!authenticationStore.bingoId) return false
          return true
        },
        delay (retries) {
          // ~55 seconds for all retries
          return retries * 1000
        },
        onFailed () {
          // wait until a new access token is generated
          disconnect()
        },
      },
      onConnected (webSocket) {
        const message = WebSocketMessage.create({
          event: {
            oneofKind: 'userConnect',
            userConnect: {
              token: apiStore.accessToken!,
              connectionDetail: getConnectionDetail(),
              groupDetails: groupsInternal,
            },
          },
        })
        webSocket.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
      },
      async onMessage (_ws, event) {
        const b = await event.data.arrayBuffer()
        const message = WebSocketMessage.fromBinary(new Uint8Array(b))
        switch (message.event.oneofKind) {
          case 'groupPresence': {
            const { connectionDetails, groupDetail } = message.event.groupPresence
            const group = findGroup(groupDetail!)
            if (group) {
              clearGhostRemovals([group])
              group.users = connectionDetails
            }
            break
          }
          case 'userJoined': {
            const { connectionDetail, groupDetail } = message.event.userJoined
            const group = findGroup(groupDetail!)
            const existing = group?.users.find((u) => u.uuid === connectionDetail!.uuid)
            if (group && existing) {
              // a rejoin cancels the pending ghost removal
              existing.ghost = false
              cancelGhostRemoval(group, existing.uuid)
            } else if (group) {
              group.users.push(connectionDetail!)
            }
            break
          }
          case 'userLeft': {
            const { connectionDetail, groupDetail } = message.event.userLeft
            const group = findGroup(groupDetail!)
            const user = group?.users.find((u) => u.uuid === connectionDetail!.uuid)
            if (group && user) {
              user.ghost = true
              scheduleGhostRemoval(group, user.uuid)
            }
            break
          }
          case 'groupUpdate':
            await notifySubscribers('groupUpdate', message.event.groupUpdate)
            break
          case 'scheduleCell':
            await notifySubscribers('scheduleCell', message.event.scheduleCell)
            break
          case 'scheduleItemCreate':
            await notifySubscribers('scheduleItemCreate', message.event.scheduleItemCreate)
            break
          case 'scheduleItemUpsert':
            await notifySubscribers('scheduleItemUpsert', message.event.scheduleItemUpsert)
            break
          case 'scheduleItemDelete':
            await notifySubscribers('scheduleItemDelete', message.event.scheduleItemDelete)
            break
        }
      },
    })
    syncRef(ws, webSocketInstance.ws, { direction: 'rtl' })
    syncRef(statusInternal, webSocketInstance.status, { direction: 'rtl' })
  }

  function disconnectInternal (): void {
    clearGhostRemovals([...ghostTimeouts.keys()])
    if (ws.value) {
      ws.value.close()
    }
    ws.value = undefined
    statusInternal.value = 'CLOSED'
  }

  const shouldConnect = ref(false)
  watch(shouldConnect, (newShouldConnect) => {
    if (newShouldConnect) {
      connectInternal()
    } else {
      disconnectInternal()
    }
  }, { immediate: true })

  const connect: WebSocketStore['connect'] = () => {
    shouldConnect.value = true
  }

  const disconnect: WebSocketStore['disconnect'] = () => {
    shouldConnect.value = false
  }

  const join: WebSocketStore['join'] = (location) => {
    const groupDetail: GroupDetail = {
      program: program.value!,
      location,
    }
    let group = groupsInternal.find((g) => isSameGroupLocation(g.location, location))
    if (!group) {
      group = {
        ...groupDetail,
        users: [
          getConnectionDetail(),
        ],
      }
      groupsInternal.push(group)
    }
    if (!ws.value || statusInternal.value !== 'OPEN') return
    const message = WebSocketMessage.create({
      event: {
        oneofKind: 'joinGroup',
        joinGroup: { groupDetail },
      },
    })
    ws.value.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
    return group
  }

  const leave: WebSocketStore['leave'] = (location) => {
    const existing = groupsInternal.findIndex((g) => isSameGroupLocation(g.location, location))
    if (existing > -1) {
      const [removed] = groupsInternal.splice(existing, 1)
      if (removed) clearGhostRemovals([removed])
    }
    if (!ws.value || statusInternal.value !== 'OPEN') return
    const message = WebSocketMessage.create({
      event: {
        oneofKind: 'leaveGroup',
        leaveGroup: {
          groupDetail: {
            program: program.value!,
            location,
          },
        },
      },
    })
    ws.value.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
  }

  const sendGroupUpdate: WebSocketStore['sendGroupUpdate'] = (location, payload) => {
    if (!ws.value || statusInternal.value !== 'OPEN') return
    const message = WebSocketMessage.create({
      event: {
        oneofKind: 'groupUpdate',
        groupUpdate: {
          groupDetail: { program: program.value!, location },
          connectionDetail: getConnectionDetail(),
          onlyRoles: [],
          ...payload,
        },
      },
    })
    ws.value.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
  }

  const sendScheduleCell: WebSocketStore['sendScheduleCell'] = (location, payload) => {
    if (!ws.value || statusInternal.value !== 'OPEN') return
    const message = WebSocketMessage.create({
      event: {
        oneofKind: 'scheduleCell',
        scheduleCell: {
          groupDetail: { program: program.value!, location },
          connectionDetail: getConnectionDetail(),
          ...payload,
        },
      },
    })
    ws.value.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
  }

  const sendScheduleItemCreate: WebSocketStore['sendScheduleItemCreate'] = (location, payload) => {
    if (!ws.value || statusInternal.value !== 'OPEN') return
    const message = WebSocketMessage.create({
      event: {
        oneofKind: 'scheduleItemCreate',
        scheduleItemCreate: {
          groupDetail: { program: program.value!, location },
          connectionDetail: getConnectionDetail(),
          ...payload,
        },
      },
    })
    ws.value.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
  }

  const sendScheduleItemUpsert: WebSocketStore['sendScheduleItemUpsert'] = (location, payload) => {
    if (!ws.value || statusInternal.value !== 'OPEN') return
    const message = WebSocketMessage.create({
      event: {
        oneofKind: 'scheduleItemUpsert',
        scheduleItemUpsert: {
          groupDetail: { program: program.value!, location },
          connectionDetail: getConnectionDetail(),
          ...payload,
        },
      },
    })
    ws.value.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
  }

  const sendScheduleItemDelete: WebSocketStore['sendScheduleItemDelete'] = (location, payload) => {
    if (!ws.value || statusInternal.value !== 'OPEN') return
    const message = WebSocketMessage.create({
      event: {
        oneofKind: 'scheduleItemDelete',
        scheduleItemDelete: {
          groupDetail: { program: program.value!, location },
          connectionDetail: getConnectionDetail(),
          ...payload,
        },
      },
    })
    ws.value.send(WebSocketMessage.toBinary(message) as Uint8Array<ArrayBuffer>)
  }

  const subscribe: WebSocketStore['subscribe'] = (event, location, handler) => {
    const subscriber: GroupSubscriber = { location, handler: handler as GroupSubscriber['handler'] }
    const eventSubscribers = subscribers.get(event) ?? new Set<GroupSubscriber>()
    eventSubscribers.add(subscriber)
    subscribers.set(event, eventSubscribers)
    return () => {
      eventSubscribers.delete(subscriber)
      if (!eventSubscribers.size) subscribers.delete(event)
    }
  }

  return {
    uuid,
    status,
    groups,
    connect,
    disconnect,
    join,
    leave,
    sendGroupUpdate,
    sendScheduleCell,
    sendScheduleItemCreate,
    sendScheduleItemUpsert,
    sendScheduleItemDelete,
    subscribe,
  }
})

hotReloadStore(useWebSocketStore)
